import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';
import { supabase } from '../../utils/supabase';
import BrandLockup from '../../components/brand/BrandLockup';

function Signup() {
  const navigate = useNavigate();
  const [schoolName, setSchoolName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: authData, error: signupError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (signupError) throw signupError;
      if (!authData.user) throw new Error('Could not create user account.');

      const { data: school, error: schoolError } = await supabase
        .from('schools')
        .insert({
          name: schoolName.trim(),
          owner_id: authData.user.id,
        })
        .select()
        .single();

      if (schoolError) throw schoolError;

      const { data: term, error: termError } = await supabase
        .from('terms')
        .insert({
          school_id: school.id,
          name: 'Third Term',
          academic_year: '2025/2026',
          status: 'active',
        })
        .select()
        .single();

      if (termError) throw termError;

      const { error: updateSchoolError } = await supabase
        .from('schools')
        .update({ current_term_id: term.id })
        .eq('id', school.id);

      if (updateSchoolError) throw updateSchoolError;

      await supabase.auth.signOut();
      localStorage.removeItem('schoolId');
      localStorage.removeItem('schoolName');
      localStorage.removeItem('sections');
      navigate('/', {
        state: {
          notice: `Account created for ${school.name}. Sign in to continue setup.`,
        },
      });
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Could not create school account.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-pattern-bg min-h-screen px-4 py-8 text-gray-900 dark:text-neutral-100">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md items-center">
        <form onSubmit={handleSubmit} className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
          <div className="mb-6">
            <BrandLockup title="Create account" />
          </div>

          <div className="space-y-4">
            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-gray-700 dark:text-neutral-200">School Name</span>
              <input
                type="text"
                value={schoolName}
                onChange={(event) => setSchoolName(event.target.value)}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
                required
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-gray-700 dark:text-neutral-200">Email</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
                required
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-gray-700 dark:text-neutral-200">Password</span>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 pr-12 outline-none focus:ring-2 focus:ring-blue-500 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(current => !current)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition hover:text-gray-700 dark:hover:text-neutral-200"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <HiOutlineEyeOff className="text-xl" /> : <HiOutlineEye className="text-xl" />}
                </button>
              </div>
            </label>
          </div>

          {error && (
            <div className="mt-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-600 dark:border-red-800 dark:bg-neutral-900 dark:text-red-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !schoolName.trim()}
            className="mt-5 w-full rounded-xl bg-blue-500 px-5 py-3 font-semibold text-white transition hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400"
          >
            {loading ? 'Creating...' : 'Create School Account'}
          </button>

          <p className="mt-5 text-center text-sm text-gray-500 dark:text-neutral-400">
            Already have an account?{' '}
            <Link to="/" className="font-bold text-blue-600 dark:text-neutral-100">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default Signup;
